import { useState } from 'react';

import { addCommas, removeCommas } from '@/utils';

import { useReward } from '@/contexts/reward-context';
import { useSabaCoinEvent } from '@/contexts/saba-coin-context';
import { CloseIcon, LoadingIcon, ExclamationIcon } from '@/components/svg-icons';
import { SabaCoinWithIcon } from '@/components/saba-coin-with-icon';
import { RewardModalWrapper } from '@/components/reward-modal-wrapper';

interface RedeemCashModalProps {
	thresholdId: number;
	redeemablePrice: number;
	requiredCoins: number;
	onClose: () => void;
}

enum RedeemButtonStatus {
	Loading = 'Loading',
	Confirm = 'Confirm',
	PointsNotEnough = 'PointsNotEnough'
}

export const RedeemCashModal = ({
	thresholdId,
	redeemablePrice,
	requiredCoins,
	onClose
}: RedeemCashModalProps) => {
	const { 
		currencyName, 
		labels,
		sabaCoinWallet, 
		fetchRewardInfoThenUpdate, 
		redeemCashWithSabaCoin,
		setPurchaseMessage 
	} = useReward();
	const { fetchSabaCoinEventData } = useSabaCoinEvent();
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const { onKafkaSet } = useReward();
	
	const currentCoins = Number(
		removeCommas(sabaCoinWallet?.AvailableFund ?? '0')
	);
	const isRedeemable = currentCoins >= requiredCoins;

	let buttonStatus = RedeemButtonStatus.Confirm;
	
	if (isLoading) {
		buttonStatus = RedeemButtonStatus.Loading;
	} else if (currentCoins < requiredCoins) {
		buttonStatus = RedeemButtonStatus.PointsNotEnough;
	}

	const redeemablePriceText = addCommas(Math.round(redeemablePrice * 1000) / 1000);

	const handleRedeem = async () => {
		if (isLoading) return;
		
		onKafkaSet({
			type: 3,
			action: 29
		});

		setIsLoading(true);
		setErrorMessage('');

		try {
			if (redeemCashWithSabaCoin) {
				await redeemCashWithSabaCoin(thresholdId - 1);
				fetchRewardInfoThenUpdate();
				fetchSabaCoinEventData();
				onClose();
				setPurchaseMessage?.(labels?.lbl_RPRedeemSuccess ?? '');
			}
		} catch (error: any) {
			console.error(error);
			setErrorMessage(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<RewardModalWrapper>
			<div className="shop-voucher-modal">
				<div 
					className="shop-voucher-modal__close" 
					onClick={onClose}
				>
					<CloseIcon />
				</div>
				<div className="shop-voucher-modal__main">
					<div className="shop-voucher-detail">
						<div className="shop-voucher-detail__header">
							<span className="shop-voucher-detail__title">{labels?.lbl_RPExchangeCashBack}</span>
							<div className="shop-voucher-detail__text">
								<span>
									{`${labels?.lbl_RPExchange} ${redeemablePriceText} ${currencyName}`}
								</span>
							</div>
							<div className="shop-voucher-detail__group">
								{labels?.lbl_RPGoToUse}
								<SabaCoinWithIcon coin={requiredCoins} />
							</div>
						</div>
						<div className="shop-voucher-detail__body reward-scroller">
							<h4>{labels?.lbl_SBCDescription01}</h4>
							<p>{labels?.lbl_SBCDescription02}</p>
						</div>
						<div className="shop-voucher-detail__footer">
							<button 
								className="reward-btn reward-btn--primary"
								type="button"
								disabled={isLoading || !isRedeemable}
								data-status={isLoading ? 'loading' : undefined}
								onClick={buttonStatus === RedeemButtonStatus.Confirm 
									? handleRedeem 
									: undefined}
							>
								{{
									[RedeemButtonStatus.PointsNotEnough]: (
										<div className="reward-btn__info">
											<span className="reward-btn__text">{labels?.lbl_RPNoSabaCoins}</span>
										</div>
									),
									[RedeemButtonStatus.Confirm]: (
										<div className="reward-btn__info">
											<span className="reward-btn__text">{labels?.lbl_RPConfirmRedeem}</span>
										</div>
									),
									[RedeemButtonStatus.Loading]: <LoadingIcon />
								}[buttonStatus]}
							</button>
							
							{errorMessage ? (
								<div className="shop-voucher-detail__error">
									<ExclamationIcon />
									{labels?.lbl_VIPGiftErrorRetry}
								</div>
							) : null}
						</div>
					</div>
					<div className="shop-voucher-icon shop-voucher-icon--coin">
						<div className="shop-coin-level shop-coin-level--2"></div>
					</div>
				</div>
			</div>
		</RewardModalWrapper>
	);
};